import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, CheckCircle2, ChevronLeft, ChevronRight, Download, ChevronDown,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ONBOARDED_AMC_EXPIRY_URL, USERS_SIMPLE_URL } from "../../constants/api";
import dayjs from "dayjs";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtCurrency = (v) =>
  v != null ? `₹${parseFloat(v).toLocaleString("en-IN")}` : "—";

const AMC_BADGE = {
  overdue: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  expiring: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  ok: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
};

const getBadgeType = (daysLeft) =>
  daysLeft < 0 ? "overdue" : daysLeft <= 30 ? "expiring" : "ok";

const DATE_PRESETS = [
  { value: "all",       label: "All Time" },
  { value: "next30",    label: "Next 30 Days" },
  { value: "next60",    label: "Next 60 Days" },
  { value: "next90",    label: "Next 90 Days" },
  { value: "next180",   label: "Next 180 Days" },
  { value: "custom",    label: "Custom Range" },
];

const resolveRange = (preset, customFrom, customTo) => {
  const today = dayjs().format("YYYY-MM-DD");
  switch (preset) {
    case "next30":  return { from_date: today, to_date: dayjs().add(30,  "day").format("YYYY-MM-DD") };
    case "next60":  return { from_date: today, to_date: dayjs().add(60,  "day").format("YYYY-MM-DD") };
    case "next90":  return { from_date: today, to_date: dayjs().add(90,  "day").format("YYYY-MM-DD") };
    case "next180": return { from_date: today, to_date: dayjs().add(180, "day").format("YYYY-MM-DD") };
    case "custom":  return (customFrom && customTo) ? { from_date: customFrom, to_date: customTo } : {};
    default:        return {};
  }
};

export default function AMCTrackerReport() {
  const { fetch }  = useApiMethods();
  const navigate   = useNavigate();

  const [data,          setData]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [datePreset,    setDatePreset]    = useState("next90");
  const [customFrom,    setCustomFrom]    = useState("");
  const [customTo,      setCustomTo]      = useState("");
  const [ownerFilter,   setOwnerFilter]   = useState("");
  const [statusFilter,  setStatusFilter]  = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [users,         setUsers]         = useState([]);
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(25);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { from_date, to_date } = resolveRange(datePreset, customFrom, customTo);
      const params = new URLSearchParams();
      if (from_date) params.append("from_date", from_date);
      if (to_date)   params.append("to_date",   to_date);
      if (ownerFilter)   params.append("assigned_to",   ownerFilter);
      if (paymentFilter) params.append("payment_status", paymentFilter);
      const url = `${ONBOARDED_AMC_EXPIRY_URL}?${params.toString()}`;
      const res = await fetch(url);
      if (res?.status === "success") setData(res.data || []);
    } catch {
      ToastMsg("Failed to load AMC report", "error");
    } finally {
      setLoading(false);
    }
  }, [datePreset, customFrom, customTo, ownerFilter, paymentFilter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    fetch(USERS_SIMPLE_URL).then(r => { if (r?.status === "success") setUsers(r.data || []); });
  }, []);
  useEffect(() => { setPage(1); }, [statusFilter, datePreset, ownerFilter, paymentFilter]);

  const filtered = statusFilter === "all" ? data :
    statusFilter === "overdue"  ? data.filter(r => r.is_overdue) :
    statusFilter === "expiring" ? data.filter(r => !r.is_overdue && r.days_left <= 30) :
    data.filter(r => !r.is_overdue && r.days_left > 30);

  const overdue    = data.filter(r => r.is_overdue).length;
  const expiring30 = data.filter(r => !r.is_overdue && r.days_left <= 30).length;
  const totalValue = data.reduce((s, r) => s + parseFloat(r.amc_value || 0), 0);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

  const exportCSV = () => {
    const headers = ["Client","AMC Start","AMC End","AMC Value","Payment Terms","Days Left","Payment Status","Assigned To"];
    const rows = filtered.map(r => [
      r.client_name, r.amc_start_date || "", r.amc_end_date, r.amc_value,
      r.payment_terms || "", r.days_left,
      r.is_overdue ? "Overdue" : `Expiring in ${r.days_left}d`,
      r.assigned_to_name || "",
    ]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `amc-tracker-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  return (
    <ContainerCard>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">AMC Tracker Report</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Track AMCs expiring or overdue across all onboarded clients</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1.5">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={loadData} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-4 mb-4 px-1">
        <span className="text-xs text-gray-500 dark:text-gray-400">Total: <span className="font-semibold text-gray-700 dark:text-gray-300">{data.length}</span></span>
        <span className="text-xs text-gray-500 dark:text-gray-400">Overdue: <span className="font-semibold text-red-600">{overdue}</span></span>
        <span className="text-xs text-gray-500 dark:text-gray-400">Expiring in 30d: <span className="font-semibold text-yellow-600">{expiring30}</span></span>
        <span className="text-xs text-gray-500 dark:text-gray-400">Value at Risk: <span className="font-semibold text-gray-700 dark:text-gray-300">{fmtCurrency(totalValue)}</span></span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        {/* Date preset */}
        <div className="relative">
          <select value={datePreset} onChange={e => { setDatePreset(e.target.value); if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); } }}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer">
            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
        {datePreset === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none" />
            <Button onClick={loadData} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
          </>
        )}
        {/* Status (overdue/expiring) */}
        <div className="relative">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none cursor-pointer">
            <option value="all">All Status</option>
            <option value="overdue">Overdue</option>
            <option value="expiring">Expiring in 30d</option>
            <option value="later">Expiring &gt; 30d</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
        {/* Payment status */}
        <div className="relative">
          <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none cursor-pointer">
            <option value="">All Payment Status</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Overdue">Overdue</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
        {/* Assigned To */}
        <div className="relative">
          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none cursor-pointer">
            <option value="">All Assigned To</option>
            {users.map(u => (
              <option key={u.id} value={String(u.id)}>
                {`${u.login_fname || ""} ${u.login_lname || ""}`.trim()}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading…</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Client</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">AMC Period</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">AMC Value</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Payment Terms</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Days Left</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Payment Status</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Assigned To</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10">
                    <div className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
                      <CheckCircle2 className="h-8 w-8 opacity-30 text-green-500" />
                      <p>No AMCs match this filter — all clear!</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((row, idx) => {
                  const badge = getBadgeType(row.days_left);
                  return (
                    <tr key={row.amc_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/onboarded/${row.client_id}`, { state: { tab: "AMC & Renewals" } })}>
                      <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200">{row.client_name}</td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">
                        {fmtDate(row.amc_start_date)} → {fmtDate(row.amc_end_date)}
                      </td>
                      <td className="px-3 py-2.5 font-medium text-gray-700 dark:text-gray-300 text-right">{fmtCurrency(row.amc_value)}</td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{row.payment_terms || "—"}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${AMC_BADGE[badge]}`}>
                          {row.is_overdue ? `${Math.abs(row.days_left)}d overdue` : `${row.days_left}d left`}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          row.payment_status === "Paid"    ? "bg-green-100 text-green-700" :
                          row.payment_status === "Overdue" ? "bg-red-100 text-red-700" :
                          "bg-yellow-100 text-yellow-700"
                        }`}>{row.payment_status}</span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{row.assigned_to_name || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Pagination footer */}
          {filtered.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none">
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {filtered.length === 0 ? "0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)}`} of {filtered.length}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={page === 1} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i-1] > 1) acc.push("…"); acc.push(p); return acc; }, [])
                  .map((p, i) => p === "…"
                    ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                    : <button key={p} onClick={() => setPage(p)} className={`min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors ${p === page ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}>{p}</button>
                  )
                }
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
              </div>
            </div>
          )}
        </div>
      )}
    </ContainerCard>
  );
}
